export interface ChartDataParams {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  sortby?: string[];
  reverseSort?: string[];
  priceKey?: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
  average?: boolean;
  averageKeys?: string[]; // New property for keys to average
  averageGroupKeys?: string[]; // New property for keys to group averages by
  averageDuplicationKeys?: string[];
  tableKeys?: string[];
}

export interface ChartData {
  keys: string[];
  unit_keys: string[];
  keysLabels: string[];
  group_by: Record<string, any[]>;
  data: Array<Record<string, any>>;
  tableData?: Array<Record<string, any>>;
}

export function FormatChartData({
  aggregationKeys,
  averageKeys,
  averageGroupKeys,
  averageDuplicationKeys,
  groupBy,
  keys,
  priceKey = "",
  volumeKey,
  tableKeys,
  sortby,
  reverseSort,
  data,
}: ChartDataParams): ChartData {
  aggregationKeys = aggregationKeys.filter(
    (key) => !averageKeys?.includes(key)
  );
  const keyData: Record<string, Record<string, any>> = {};
  const distinctGroupCombinations: string[] = [];
  const groupByKey: Record<string, any[]> = {};

  const units: string[] = [
    priceKey,
    volumeKey,
    ...aggregationKeys,
    ...(averageKeys || []),
  ];

  for (const d of data) {
    const groupKey: string = groupBy.map((key) => String(d[key])).join("_");
    for (const key of groupBy) {
      if (!(key in groupByKey)) {
        groupByKey[key] = [];
      }
      if (!groupByKey[key].includes(d[key])) {
        groupByKey[key].push(d[key]);
      }
    }
    if (!distinctGroupCombinations.includes(groupKey)) {
      distinctGroupCombinations.push(groupKey);
    }
  }

  for (const d of data) {
    const key: string = keys.map((k) => String(d[k])).join("_");
    const groupKey: string = groupBy.map((g) => String(d[g])).join("_");

    if (!(key in keyData)) {
      keyData[key] = keys.reduce((acc, k) => {
        acc[k] = d[k];
        return acc;
      }, {} as Record<string, any>);

      for (const group of distinctGroupCombinations) {
        keyData[key][`${group}_${volumeKey}`] = 0;
        keyData[key][`${group}_${priceKey}`] = 0;
        for (const agg of aggregationKeys) {
          keyData[key][`${group}_${agg}`] = 0;
        }
        if (averageKeys) {
          for (const avg of averageKeys) {
            keyData[key][`${group}_${avg}`] = 0;
            keyData[key][`${group}_${avg}_count`] = 0;
          }
        }
      }
    }

    for (const agg of aggregationKeys) {
      if (d[agg] !== null && !(typeof d[agg] === "number" && isNaN(d[agg]))) {
        keyData[key][`${groupKey}_${agg}`] += d[agg];
      }
    }
    if (
      d[volumeKey] !== null &&
      !(typeof d[volumeKey] === "number" && isNaN(d[volumeKey]))
    ) {
      keyData[key][`${groupKey}_${volumeKey}`] += d[volumeKey];
      if (
        d[priceKey] !== null &&
        !(typeof d[priceKey] === "number" && isNaN(d[priceKey]))
      ) {
        keyData[key][`${groupKey}_${priceKey}`] += d[priceKey] * d[volumeKey];
      }
    }
    if (averageKeys) {
      for (const avg of averageKeys) {
        if (d[avg] !== null && !(typeof d[avg] === "number" && isNaN(d[avg]))) {
          keyData[key][`${groupKey}_${avg}`] += d[avg];
          keyData[key][`${groupKey}_${avg}_count`] += 1;
          if (averageDuplicationKeys && averageDuplicationKeys.length > 0) {
            if (!keyData[key][`${groupKey}_${avg}_set`]) {
              keyData[key][`${groupKey}_${avg}_set`] = new Set();
            }
            keyData[key][`${groupKey}_${avg}_set`].add(
              `${groupKey}_${avg}_${averageDuplicationKeys
                ?.map((k) => String(d[k]))
                .join("_")}`
            );
          }
        }
      }
    }
  }

  const finalRes: Array<Record<string, any>> = [];
  for (const key in keyData) {
    const res = keyData[key];
    for (const group of distinctGroupCombinations) {
      if (res[`${group}_${volumeKey}`] > 0) {
        res[`${group}_${priceKey}`] =
          res[`${group}_${priceKey}`] / res[`${group}_${volumeKey}`];
      }
      if (averageKeys) {
        for (const avg of averageKeys) {
          res[`${group}_${avg}`] =
            res[`${group}_${avg}`] / res[`${group}_${avg}_set`]?.size || 0;
        }
      }
    }
    finalRes.push(res);
  }

  if (sortby) {
    finalRes.sort((a, b) => {
      for (const key of sortby) {
        if (a[key] > b[key]) return -1;
        else if (a[key] < b[key]) return 1;
      }
      return 0;
    });
  }
  if (reverseSort) {
    finalRes.sort((a, b) => {
      for (const key of reverseSort) {
        if (a[key] > b[key]) return 1;
        else if (a[key] < b[key]) return -1;
      }
      return 0;
    });
  }

  let tableData: Array<Record<string, any>> = [];

  if (tableKeys && tableKeys.length > 0) {
    const tableKeyData: Record<string, Record<string, any>> = {};

    for (const d of data) {
      const tableKey: string = tableKeys.map((k) => String(d[k])).join("_");
      const groupKey: string = groupBy.map((g) => String(d[g])).join("_");

      if (!(tableKey in tableKeyData)) {
        tableKeyData[tableKey] = tableKeys.reduce((acc, k) => {
          acc[k] = d[k];
          return acc;
        }, {} as Record<string, any>);

        for (const group of distinctGroupCombinations) {
          tableKeyData[tableKey][`${group}_${volumeKey}`] = 0;
          tableKeyData[tableKey][`${group}_${priceKey}`] = 0;
          for (const agg of aggregationKeys) {
            tableKeyData[tableKey][`${group}_${agg}`] = 0;
          }
          if (averageKeys) {
            for (const avg of averageKeys) {
              tableKeyData[tableKey][`${group}_${avg}`] = 0;
              tableKeyData[tableKey][`${group}_${avg}_count`] = 0;
            }
          }
        }
      }

      for (const agg of aggregationKeys) {
        if (d[agg] !== null && !(typeof d[agg] === "number" && isNaN(d[agg]))) {
          tableKeyData[tableKey][`${groupKey}_${agg}`] += d[agg];
        }
      }
      if (
        d[volumeKey] !== null &&
        !(typeof d[volumeKey] === "number" && isNaN(d[volumeKey]))
      ) {
        tableKeyData[tableKey][`${groupKey}_${volumeKey}`] += d[volumeKey];
        if (
          d[priceKey] !== null &&
          !(typeof d[priceKey] === "number" && isNaN(d[priceKey]))
        ) {
          tableKeyData[tableKey][`${groupKey}_${priceKey}`] +=
            d[priceKey] * d[volumeKey];
        }
      }
      if (averageKeys) {
        for (const avg of averageKeys) {
          if (
            d[avg] !== null &&
            !(typeof d[avg] === "number" && isNaN(d[avg]))
          ) {
            tableKeyData[tableKey][`${groupKey}_${avg}`] += d[avg];
            tableKeyData[tableKey][`${groupKey}_${avg}_count`] += 1;
            if (averageDuplicationKeys && averageDuplicationKeys.length > 0) {
              if (!tableKeyData[tableKey][`${groupKey}_${avg}_set`]) {
                tableKeyData[tableKey][`${groupKey}_${avg}_set`] = new Set();
              }
              tableKeyData[tableKey][`${groupKey}_${avg}_set`].add(
                `${groupKey}_${avg}_${averageDuplicationKeys
                  ?.map((k) => String(d[k]))
                  .join("_")}`
              );
            }
          }
        }
      }
    }

    for (const key in tableKeyData) {
      const res = tableKeyData[key];
      for (const group of distinctGroupCombinations) {
        if (res[`${group}_${volumeKey}`] > 0) {
          res[`${group}_${priceKey}`] =
            res[`${group}_${priceKey}`] / res[`${group}_${volumeKey}`];
        }
        if (averageKeys) {
          for (const avg of averageKeys) {
            res[`${group}_${avg}`] =
              res[`${group}_${avg}`] / res[`${group}_${avg}_set`]?.size || 0;
          }
        }
      }
      tableData.push(res);
    }

    if (sortby) {
      tableData.sort((a, b) => {
        for (const key of sortby) {
          if (a[key] > b[key]) return -1;
          else if (a[key] < b[key]) return 1;
        }
        return 0;
      });
    }
    if (reverseSort) {
      tableData.sort((a, b) => {
        for (const key of reverseSort) {
          if (a[key] > b[key]) return 1;
          else if (a[key] < b[key]) return -1;
        }
        return 0;
      });
    }
  }

  return {
    keys: distinctGroupCombinations,
    unit_keys: units,
    keysLabels: distinctGroupCombinations.map((key) =>
      key.replace(/_/g, " ").replace("undefined", "")
    ),
    group_by: groupByKey,
    data: finalRes,
    tableData: tableData,
  };
}

export function FormatChartDataWithAverages({
  aggregationKeys,
  groupBy,
  keys,
  priceKey,
  volumeKey,
  data,
}: {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  priceKey: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
}): {
  keys: string[];
  keysLabels: string[];
  unit_keys: string[];
  group_by: Record<string, any[]>;

  data: Array<Record<string, any>>;
} {
  const keyData: Record<string, Record<string, any>> = {};
  const distinctGroupCombinations: string[] = [];
  const groupByKey: Record<string, any[]> = {};

  const units: string[] = [priceKey, volumeKey, ...aggregationKeys];

  for (const d of data) {
    const groupKey: string = groupBy.map((key) => String(d[key])).join("_");
    for (const key of groupBy) {
      if (!(key in groupByKey)) {
        groupByKey[key] = [];
      }
      if (!groupByKey[key].includes(d[key])) {
        groupByKey[key].push(d[key]);
      }
    }
    if (!distinctGroupCombinations.includes(groupKey)) {
      distinctGroupCombinations.push(groupKey);
    }
  }

  for (const d of data) {
    const key: string = keys.map((k) => String(d[k])).join("_");
    const groupKey: string = groupBy.map((g) => String(d[g])).join("_");

    if (!(key in keyData)) {
      keyData[key] = keys.reduce((acc, k) => {
        acc[k] = d[k];
        return acc;
      }, {} as Record<string, any>);

      for (const group of distinctGroupCombinations) {
        keyData[key][`${group}_${volumeKey}`] = 0;
        keyData[key][`${group}_${priceKey}`] = 0;
        keyData[key][`${group}_${volumeKey}_count`] = 0;
        for (const agg of aggregationKeys) {
          keyData[key][`${group}_${agg}`] = 0;
        }
      }
    }

    for (const agg of aggregationKeys) {
      if (d[agg] !== null && !(typeof d[agg] === "number" && isNaN(d[agg]))) {
        keyData[key][`${groupKey}_${agg}`] += d[agg];
      }
    }
    if (
      d[volumeKey] !== null &&
      !(typeof d[volumeKey] === "number" && isNaN(d[volumeKey]))
    ) {
      keyData[key][`${groupKey}_${volumeKey}`] += d[volumeKey];
      keyData[key][`${groupKey}_${volumeKey}_count`] += 1;
      if (
        d[priceKey] !== null &&
        !(typeof d[priceKey] === "number" && isNaN(d[priceKey]))
      ) {
        keyData[key][`${groupKey}_${priceKey}`] += d[priceKey] * d[volumeKey];
      }
    }
  }

  const finalRes: Array<Record<string, any>> = [];
  for (const key in keyData) {
    const res = keyData[key];
    for (const group of distinctGroupCombinations) {
      if (res[`${group}_${volumeKey}_count`] > 0) {
        res[`${group}_${volumeKey}`] =
          res[`${group}_${volumeKey}`] / res[`${group}_${volumeKey}_count`];
        for (const agg of aggregationKeys) {
          res[`${group}_${agg}`] =
            res[`${group}_${agg}`] / res[`${group}_${volumeKey}_count`];
        }
      }
      if (res[`${group}_${volumeKey}`] > 0) {
        res[`${group}_${priceKey}`] =
          res[`${group}_${priceKey}`] / res[`${group}_${volumeKey}`];
      }
    }
    finalRes.push(res);
  }

  return {
    keys: distinctGroupCombinations,
    keysLabels: distinctGroupCombinations.map((key) =>
      key.replace(/_/g, " ").replace("undefined", "")
    ),
    unit_keys: units,
    group_by: groupByKey,
    data: finalRes,
  };
}

export function ProcessChartData({
  aggregationKeys,
  groupBy,
  keys,
  priceKey,
  volumeKey,
  data,
  average = false,
}: {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  priceKey: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
  average?: boolean;
}): {
  keys: string[];
  keysLabels: string[];
  unit_keys: string[];
  group_by: Record<string, any[]>;

  data: Array<Record<string, any>>;
} {
  if (average) {
    return FormatChartDataWithAverages({
      aggregationKeys,
      groupBy,
      keys,
      priceKey,
      volumeKey,
      data,
    });
  } else {
    return FormatChartData({
      aggregationKeys,
      groupBy,
      keys,
      priceKey,
      volumeKey,
      data,
    });
  }
}
